<script setup lang="ts">
import { computed } from 'vue'
import type { AssetPositionRow, AccountSummary } from '../api/client'
import type { ProvisionalLine } from '../lib/provisionalOverlay'

const props = withDefaults(defineProps<{
  positions: AssetPositionRow[]
  account: AccountSummary
  provisionalLines?: ProvisionalLine[]
  portfolioAdjustmentGbp?: number
}>(), { provisionalLines: () => [], portfolioAdjustmentGbp: 0 })

// Spec 2026-10-06: a BUY's cash is already with HL — it stays out of the cash
// row in every phase until the reconcile settles it ("Committed to X" before the
// strike; afterwards the bought units appear as a position row instead). The
// total only moves for priced deals (portfolioAdjustmentGbp).
const isCashOut = (l: ProvisionalLine) => l.kind === 'committed' || l.kind === 'bought'
const footerLines = computed(() => props.provisionalLines.filter(l => l.kind !== 'bought'))
const committedGbp = computed(() => props.provisionalLines.filter(isCashOut).reduce((s, l) => s + l.amountGbp, 0))
const displayCash = computed(() => props.account.cashGbp - committedGbp.value)
const displayTotal = computed(() => props.account.netPortfolioValue + props.portfolioAdjustmentGbp)

function fmtGbp(v: number): string {
  return '£' + v.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtUnits(v: number): string {
  return v.toLocaleString('en-GB', { maximumFractionDigits: 0 })
}

function fmtMark(v: number): string {
  return '£' + v.toFixed(2)
}

function unrealisedClass(v: number): string {
  if (v > 0) return 'pnl-positive'
  if (v < 0) return 'pnl-negative'
  return ''
}

function unrealisedSign(v: number): string {
  return v >= 0 ? '+' : ''
}

const displayName: Record<string, string> = {
  BGA: 'BGA Acc',
  BGA_INC: 'BGA Inc',
}

function assetLabel(t: string): string {
  return displayName[t] ?? t
}
</script>

<template>
  <div class="breakdown">
    <div class="breakdown-title">Positions</div>
    <table class="breakdown-table">
      <thead>
        <tr>
          <th>Asset</th>
          <th class="num">Units</th>
          <th class="num">Mark</th>
          <th class="num">Value</th>
          <th class="num">Unrealised</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="p in positions" :key="p.assetType">
          <td class="asset">{{ assetLabel(p.assetType) }}</td>
          <td class="num">{{ fmtUnits(p.units) }}</td>
          <td class="num mark">{{ fmtMark(p.markPrice) }}</td>
          <td class="num">{{ fmtGbp(p.markValue) }}</td>
          <td class="num" :class="unrealisedClass(p.unrealisedPnl)">
            {{ unrealisedSign(p.unrealisedPnl) }}{{ fmtGbp(p.unrealisedPnl) }}
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr class="footer-row">
          <td>Cash</td>
          <td colspan="3"></td>
          <td class="num">{{ fmtGbp(displayCash) }}</td>
        </tr>
        <tr v-for="l in footerLines" :key="l.key" class="footer-row provisional-row">
          <td colspan="4">{{ l.label }} <span v-if="l.tag" class="prov-tag">{{ l.tag }}</span></td>
          <td class="num">{{ l.amountGbp ? fmtGbp(l.amountGbp) : '' }}</td>
        </tr>
        <tr class="footer-row">
          <td>Accrued</td>
          <td colspan="3"></td>
          <td class="num pnl-negative">-{{ fmtGbp(account.accruedCosts) }}</td>
        </tr>
        <tr class="total-row">
          <td>Total</td>
          <td colspan="3"></td>
          <td class="num">{{ fmtGbp(displayTotal) }}</td>
        </tr>
      </tfoot>
    </table>
  </div>
</template>

<style scoped>
.breakdown {
  background: #0d1117;
  border: 1px solid #1c2128;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 12px;
}
.breakdown-title {
  font-size: 10px;
  color: #888;
  font-family: 'JetBrains Mono', monospace;
  padding-bottom: 6px;
  border-bottom: 1px solid #1c2128;
  margin-bottom: 6px;
}
.breakdown-table {
  width: 100%;
  border-collapse: collapse;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
}
.breakdown-table th {
  color: #666;
  font-weight: normal;
  padding: 4px 8px;
  text-align: left;
}
.breakdown-table td {
  padding: 4px 8px;
  color: #c9d1d9;
}
.breakdown-table .num { text-align: right; }
.breakdown-table .asset { font-weight: 500; color: #58a6ff; }
.breakdown-table .mark {
  animation: pulse-mark 2s ease-in-out infinite;
}
@keyframes pulse-mark {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}
.pnl-positive { color: #4caf50; }
.pnl-negative { color: #f44336; }
.footer-row td {
  border-top: 1px solid #1c2128;
  color: #888;
}
.provisional-row td { color: #d29922; }
.prov-tag {
  font-size: 9px;
  color: #888;
  border: 1px solid #30363d;
  border-radius: 3px;
  padding: 0 3px;
  margin-left: 4px;
}
.total-row td {
  border-top: 1px solid #30363d;
  font-weight: 600;
  color: #fff;
  padding-top: 6px;
}

@media (max-width: 768px) {
  .breakdown-table { font-size: 10px; }
  .breakdown-table th, .breakdown-table td { padding: 3px 4px; }
}
</style>
